/**
 * Bundle & Save callout: sync copy with Rebuy / Shopify cart (total quantity,
 * excluding selling-plan lines). Listens on document only — does not touch ATC or cart UI.
 */
(function () {
  'use strict';

  /**
   * @param {Record<string, unknown> | null | undefined} shopifyCart
   * @returns {number}
   */
  function qualifyingQuantity(shopifyCart) {
    if (!shopifyCart || !Array.isArray(shopifyCart.items)) return 0;
    let sum = 0;
    for (let i = 0; i < shopifyCart.items.length; i++) {
      const item = shopifyCart.items[i];
      if (item && item.selling_plan_allocation) continue;
      sum += Number(item.quantity) || 0;
    }
    return sum;
  }

  /**
   * @returns {Record<string, unknown> | null}
   */
  function shopifyCartFromRebuyModule() {
    try {
      const C = window.Rebuy && window.Rebuy.Cart;
      if (!C) return null;
      const raw = typeof C.cart === 'function' ? C.cart() : C.cart;
      if (raw && Array.isArray(raw.items)) return raw;
      if (raw && raw.cart && Array.isArray(raw.cart.items)) return raw.cart;
    } catch (_e) {
      /* ignore */
    }
    return null;
  }

  /**
   * @param {CustomEvent} event
   * @returns {Record<string, unknown> | null}
   */
  function shopifyCartFromRebuyEvent(event) {
    const detail = event && event.detail;
    const cartModule = detail && detail.cart;
    if (!cartModule) return null;
    const inner = cartModule.cart;
    if (inner && Array.isArray(inner.items)) return inner;
    if (cartModule.items && Array.isArray(cartModule.items)) return cartModule;
    return null;
  }

  /**
   * @param {{
   *   itemsRequired: number;
   *   headingDefault: string;
   *   headingComplete: string;
   *   bodyDefault: string;
   * }} config
   * @param {number} qty
   * @returns {{ complete: boolean; headingIncomplete: string; body: string; successLine: string }}
   */
  function computeCopy(config, qty) {
    const n = Math.max(2, Math.min(99, Number(config.itemsRequired) || 3));
    if (qty >= n) {
      return {
        complete: true,
        headingIncomplete: '',
        body: '',
        successLine: config.headingComplete || '',
      };
    }
    let body = config.bodyDefault || '';
    if (qty > 0) {
      const remaining = n - qty;
      if (remaining === 1) body = 'Add 1 more item!';
      else body = 'Add ' + remaining + ' more items';
    }
    return {
      complete: false,
      headingIncomplete: config.headingDefault || '',
      body,
      successLine: '',
    };
  }

  /**
   * @param {HTMLElement} root
   * @param {{
   *   itemsRequired: number;
   *   headingDefault: string;
   *   headingComplete: string;
   *   bodyDefault: string;
   * }} config
   * @param {number} qty
   */
  function applyState(root, config, qty) {
    const state = computeCopy(config, qty);
    const incompleteWrap = root.querySelector('[data-dadbrand-bundle-incomplete]');
    const successWrap = root.querySelector('[data-dadbrand-bundle-success-only]');
    const successTextEl = root.querySelector('[data-dadbrand-bundle-success-text]');
    const headingEl = root.querySelector('[data-dadbrand-bundle-heading]');
    const bodyEl = root.querySelector('[data-dadbrand-bundle-body]');
    const sepEl = root.querySelector('[data-dadbrand-bundle-sep]');

    if (state.complete) {
      root.classList.add('dadbrand-bundle-save--complete');
      if (incompleteWrap) incompleteWrap.setAttribute('hidden', 'hidden');
      if (successWrap) successWrap.removeAttribute('hidden');
      if (successTextEl) successTextEl.textContent = state.successLine;
      const aria = state.successLine || config.headingComplete || '';
      if (aria) root.setAttribute('aria-label', aria);
      else root.removeAttribute('aria-label');
    } else {
      root.classList.remove('dadbrand-bundle-save--complete');
      if (incompleteWrap) incompleteWrap.removeAttribute('hidden');
      if (successWrap) successWrap.setAttribute('hidden', 'hidden');
      if (successTextEl) successTextEl.textContent = '';
      if (headingEl) headingEl.textContent = state.headingIncomplete;
      if (bodyEl) bodyEl.textContent = state.body;
      if (sepEl) {
        if (state.body) sepEl.removeAttribute('hidden');
        else sepEl.setAttribute('hidden', 'hidden');
      }
      const aria = state.headingIncomplete || '';
      if (aria) root.setAttribute('aria-label', aria);
      else root.removeAttribute('aria-label');
    }
  }

  /**
   * @param {HTMLElement} root
   */
  function bindRoot(root) {
    const id = root.getAttribute('data-config-id');
    if (!id) return;
    const script = document.getElementById(id);
    if (!script || !script.textContent) return;
    /** @type {unknown} */
    let parsed;
    try {
      parsed = JSON.parse(script.textContent);
    } catch (_e) {
      return;
    }
    if (!parsed || typeof parsed !== 'object') return;
    const config = /** @type {Parameters<typeof applyState>[1]} */ (parsed);

    function syncFromCart(shopifyCart) {
      const qty = qualifyingQuantity(shopifyCart);
      applyState(root, config, qty);
    }

    function syncFromEvent(event) {
      const cart = shopifyCartFromRebuyEvent(event);
      if (cart) syncFromCart(cart);
    }

    function syncBestEffort() {
      const c = shopifyCartFromRebuyModule();
      if (c) syncFromCart(c);
    }

    document.addEventListener('rebuy:cart.change', syncFromEvent);
    document.addEventListener('rebuy:cart.ready', syncFromEvent);
    document.addEventListener('rebuy.loaded', syncBestEffort);

    syncBestEffort();
  }

  function init() {
    document.querySelectorAll('[data-dadbrand-bundle-save]').forEach(function (el) {
      if (el instanceof HTMLElement) bindRoot(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
